// ----------------------------------------------------------------------------
// Product Form Web Component: iamSellingPlanInput <iam-selling-plan-input>
// Extends: iamFormElement
// ----------------------------------------------------------------------------
// The Selling Plan Input is responsible for handling the selling plan selection UI.
// ----------------------------------------------------------------------------
// Event Listeners:
// - `productForm:variant:changed` → Dynamically refreshes selling plans on variant change
// ----------------------------------------------------------------------------
// Gotchas:
// - Unknown support with Preorders, multiple selling plan groups, or plans with >1 option input
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import iamFormElement from 'scripts/classes/iamFormElement';
import { promoPriceOverrides } from '../promo-price-override/promo-price-overrides';
import registerCustomElement from 'scripts/helpers/register-custom-element';

// Styles
import './_ordergroove-offer.scss';

// Additional Features
import './ordergroove-offer--show-frequency.js'; // Show frequency in the OrderGroove offer

class iamOrdergrooveOffer extends iamFormElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.ogOffer = this.querySelector('og-offer');
    this.sellingPlanInput = this.querySelector('[name="selling_plan"]');

    // Broadcast Selling Plan Changes
    if (this.sellingPlanInput) {
      var triggerChange = () => {
        iamotaEventHandler.trigger('productForm:sellingPlanInput:changed', {
          sellingPlan: this.sellingPlanInput.value
        }, {
          context: this.iamForm
        });
      };

      // Trigger on change
      this.sellingPlanInput.addEventListener('change', triggerChange);

      // Optional: trigger on input if you want more immediate feedback
      this.sellingPlanInput.addEventListener('input', triggerChange);
    }
    super.connectedCallback();
  }
  onFormInit() {
    iamotaEventHandler.subscribe('productForm:variant:changed', this.onVariantChange.bind(this), {
      context: this.iamForm,
      source: "ordergroove-offer.js"
    });

    // Initialize the OrderGroove offer component
    var variant = this.iamForm.getCurrentVariant();
    document.addEventListener('og-receive-offer', () => {
      // occurs when offers is ready to be rendered in the UI
      this.onVariantChange({
        variant
      });
    });
    this.onVariantChange({
      variant
    });
  }
  onVariantChange(_ref) {
    var {
      variant
    } = _ref;
    if (!variant) return;
    this.ogOffer.setAttribute('product', variant.id);

    /**
     * Subscription price & discount override
     */
    if (promoPriceOverrides.subscriptionPromo) {
      var subscriptionDiscount = promoPriceOverrides.subscriptionPromo.getDiscountPercent(variant.sku);

      // Subscription Discount percent label
      var discountLabels = this.ogOffer.querySelectorAll('og-incentive-text[from="DiscountPercent"][subscription]');
      discountLabels.forEach(label => {
        label.innerHTML = "".concat(subscriptionDiscount, "%");
      });

      // Subscription price elements
      var subAdjustedPriceElements = this.ogOffer.querySelectorAll('og-price[subscription]');
      var adjustedPrice = promoPriceOverrides.subscriptionPromo.calculateDiscountedPrice(variant.price, subscriptionDiscount);
      subAdjustedPriceElements.forEach(priceEl => {
        var adjustedPriceEl = document.createElement('span');
        adjustedPriceEl.slot = 'fallback';
        adjustedPriceEl.textContent = adjustedPrice;
        priceEl.replaceChildren(adjustedPriceEl);
      });
      var originalPriceElements = this.ogOffer.querySelectorAll('og-price:not([subscription])');
      var originalPrice = promoPriceOverrides.subscriptionPromo.formatCentsToDollars(variant.price);
      originalPriceElements.forEach(priceEl => {
        var originalPriceEl = document.createElement('span');
        originalPriceEl.slot = 'fallback';
        originalPriceEl.textContent = originalPrice;
        priceEl.replaceChildren(originalPriceEl);
      });
    }

    /**
     * One-Time purchase (non-subscription) price & discount override
     */
    if (promoPriceOverrides.oneTimePurchasePromo) {
      var oneTimePurchaseDiscountPercent = promoPriceOverrides.oneTimePurchasePromo.getDiscountPercent(variant.sku);

      // Only apply if the discount is greater than 0
      if (oneTimePurchaseDiscountPercent) {
        var oneTimePurchasePriceElements = this.ogOffer.querySelectorAll('og-price[regular].og-price--one-time');
        var oneTimePurchasePrice = promoPriceOverrides.oneTimePurchasePromo.calculateDiscountedPrice(variant.price, oneTimePurchaseDiscountPercent);
        oneTimePurchasePriceElements.forEach(priceEl => {
          var oneTimePurchasePriceEl = document.createElement('span');
          oneTimePurchasePriceEl.slot = 'fallback';
          oneTimePurchasePriceEl.textContent = oneTimePurchasePrice;
          priceEl.replaceChildren(oneTimePurchasePriceEl);
        });

        // Discount percent label
        var _discountLabels = this.ogOffer.querySelectorAll('og-incentive-text[from="DiscountPercent"][regular]');
        _discountLabels.forEach(label => {
          label.innerHTML = "".concat(oneTimePurchaseDiscountPercent, "%");
        });

        // show the original price and discount percent elements when one time is also discounted.
        this.ogOffer.querySelectorAll('.selling-plan__option--one-time .hide').forEach(el => el.classList.remove('hide'));
      }
    }
  }
}
registerCustomElement('iam-ordergroove-offer', iamOrdergrooveOffer);
export default iamOrdergrooveOffer;